import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { PageHeader } from "@bfc-software/pallet";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
  CREATING_SESSION,
} from "@bfc-software/app-toolbox";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";
import { PrimarySidebar } from "@bfc-software/bfc-web-ui/ui/shared/primary-sidebar";
import { LocationSelector } from "@bfc-software/bfc-web-ui/ui/shared/location-selector";
import { useContext, useEffect } from "react";
import {
  useCommandPalette,
  navigationCommand,
} from "@bfc-software/bfc-web-ui/ui/shared/command-palette";
import { TFunction } from "i18next";

import { internalToolsEnabled } from "@bfc-software/bfc-web-ui/lib/internal-tools";

import styles from "./_layout.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout",
)({
  component: RouteComponent,
  staticData: {
    crumb: (match: MakeRouteMatchUnion) => {
      const params = match.params as { location?: string };
      return { label: params.location ?? "" };
    },
  },
});

export type Crumb = {
  label: string;
  to?: string;
  params?: Record<string, string>;
};

type CrumbEnabledRouteStaticData = {
  crumb: (match: MakeRouteMatchUnion, t: TFunction) => Crumb | Crumb[];
};

function getCrumbsFromMatch(
  routeDef: MakeRouteMatchUnion,
  t: TFunction,
): Array<[string, Crumb]> {
  const staticData = (routeDef.staticData ?? {}) as CrumbEnabledRouteStaticData;
  if (typeof staticData.crumb !== "function") {
    return [];
  }
  const result = staticData.crumb(routeDef, t);
  const crumbs = Array.isArray(result) ? result : [result];
  return crumbs.map((crumb, i) => [`${routeDef.id}:${i}`, crumb]);
}

export function useCrumbs(): Array<[string, Crumb]> {
  const params = Route.useParams();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  return useRouterState({
    select: (s) => s.matches.flatMap((m) => getCrumbsFromMatch(m, t)),
  });
}

function renderCrumb([id, crumb]: [string, Crumb], isLast: boolean) {
  return (
    <div key={id} className={styles.crumb}>
      {crumb.to && !isLast ? (
        <Link to={crumb.to} params={crumb.params} className={styles.crumbLink}>
          {crumb.label}
        </Link>
      ) : (
        <span className={styles.crumbLabel}>{crumb.label}</span>
      )}
    </div>
  );
}

function RouteComponent() {
  const params = Route.useParams();
  const crumbs = useCrumbs();
  const { state, selectLocation } = useContext(AuthContext);
  const { register } = useCommandPalette();
  const navigate = useNavigate();

  /**
   * Inside the Engine Room the URL owns the warehouse and the session follows
   * it, which is what makes a bookmark work. Reconciliation happens here and
   * nowhere else: the header selector only navigates, because two effects
   * reconciling in opposite directions loop.
   *
   * A warehouse the organisation does not have goes to the chooser. Re-minting
   * against it would 401 on every pass, and rendering it shows an empty tool
   * that reads as an empty warehouse.
   *
   * CREATING_SESSION counts as out of sync. The organisation's warehouses are
   * known in that state, but the AAA token is still the previous warehouse's -
   * the org layout keeps this subtree mounted while the session is re-minted,
   * so without this the screen below would fetch against the warehouse being
   * left.
   */
  const authenticated = state.status === AUTHENTICATED;
  const remintingSession = state.status === CREATING_SESSION;
  const orgKnown = authenticated || remintingSession;

  const sessionWarehouse = authenticated ? state.locationId : null;
  const orgWarehouses = orgKnown
    ? AuthUtils.getLocations(state, state.orgId)
    : [];
  const urlWarehouseIsKnown = orgWarehouses.some(
    (warehouse) => warehouse.warehouseId === params.location,
  );
  const warehouseIsUnknown = orgKnown && !urlWarehouseIsKnown;
  const needsWarehouseSync =
    urlWarehouseIsKnown && sessionWarehouse !== params.location;

  useEffect(() => {
    if (!authenticated) return;
    if (!needsWarehouseSync) return;

    selectLocation(params.location);
  }, [authenticated, needsWarehouseSync, params.location, selectLocation]);

  useEffect(() => {
    if (!warehouseIsUnknown) return;

    navigate({
      to: "/$lang/orgs/$org/internal",
      params: { lang: params.lang, org: params.org },
      replace: true,
    });
  }, [warehouseIsUnknown, navigate, params.lang, params.org]);

  useEffect(() => {
    // Every command below points into _internal. Registering them for someone
    // who cannot open them would surface the tooling in search results and then
    // 404 on selection.
    if (!internalToolsEnabled()) return;

    register([
      navigationCommand({
        id: "org-location-home",
        title: "global.commandPalette.commands.orgHome",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/home" },
        keywords: ["home"],
      }),
      navigationCommand({
        id: "org-location-ftp",
        title: "global.commandPalette.commands.orgFTP",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/ftp" },
        keywords: ["ftp", "history"],
      }),

      navigationCommand({
        id: "org-location-task-plans",
        title: "global.commandPalette.commands.orgTaskPlans",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/task-plans" },
        keywords: ["tasks", "plans", "task-plans"],
      }),

      navigationCommand({
        id: "org-location-selector-sessions",
        title: "global.commandPalette.commands.orgSelectorSessions",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/selector-sessions" },
        keywords: ["selectors", "sessions", "shift"],
      }),

      navigationCommand({
        id: "org-location-webhooks",
        title: "global.commandPalette.commands.orgWebhooks",
        to: {
          to: "/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints",
        },
        keywords: ["webhooks", "endpoints"],
      }),

      navigationCommand({
        id: "org-location-ftp-config",
        title: "global.commandPalette.commands.orgFTPConfig",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/ftp-config" },
        keywords: ["ftp", "config", "configuration"],
      }),
    ]);
  }, [params.lang, params.location, params.org, register]);

  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const orgId = AuthUtils.getOrgIdByStub(state, params.org);
  const orgName = orgId ? AuthUtils.getOrgName(state, orgId) : null;

  return (
    <>
      <AppHeader
        nav={<PrimarySidebar />}
        actions={<LocationSelector className={styles.warehouseMenu} />}
      />
      <PageHeader title={orgName ?? t("header")}>
        <div className={styles.crumbs}>
          {crumbs.map((crumb, i) =>
            renderCrumb(crumb, i === crumbs.length - 1),
          )}
        </div>
      </PageHeader>
      {/*
        Held back until the session matches the warehouse in the URL, so a
        screen never loads against the one being left - or against a warehouse
        the organisation does not have, which is on its way to the chooser.
      */}
      {needsWarehouseSync || warehouseIsUnknown ? (
        <p className={styles.switching}>{t("loadingWarehouse")}</p>
      ) : (
        <Outlet />
      )}
    </>
  );
}
